import React, { useState } from 'react';
import { Box, Button } from '@mui/material';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import { allHangerHardware, StandardConnectingHardwareModel } from '@paragon/contract-library';
import { NewHangerPrice } from '@paragon/data-connections';
import { hasPricesEditPermission } from '@paragon/login-tools';
import { NumericField } from '@paragon/material';
import { getConnectingHardwareName } from '@paragon/material-lists';

import { insertHangerPrice } from '../../actions/hangerPriceActions';
import { CallToUpgrade } from '../CallToUpgrade';
import { useAppDispatch, useAppSelector } from '../hooks';
import { PriceActionBar } from '../PriceActionBar';

export function InsertHangerPrice({
  onClose,
  onHeightChange,
}: {
  readonly onClose: () => void;
  readonly onHeightChange: (height: number) => void;
}) {
  const dispatch = useAppDispatch();
  const hasEditPermission = useAppSelector(hasPricesEditPermission);

  const [hanger, setHangerHardware] = useState<StandardConnectingHardwareModel | null>(null);
  const [price, setPrice] = useState<number>(0);
  const [notes, setNotes] = useState<string | null>(null);

  async function onInsertClick(): Promise<void> {
    if (hanger === null) throw new Error('Hanger type not specified');
    const newHangerPrice: NewHangerPrice = { hanger, price, notes };
    if (await dispatch(insertHangerPrice(newHangerPrice))) onClose();
  }

  const insertDisabled = hanger === null || price === 0 || !hasEditPermission;

  return (
    <PriceActionBar
      onClose={onClose}
      onHeightChange={onHeightChange}
      sx={{
        gap: 2,

        '& > *': {
          flex: '1 0 200px',
          margin: '0!important',
        },
      }}
    >
      <PropertySelectField<StandardConnectingHardwareModel>
        property="Hanger"
        value={hanger}
        submit={(hangerHardware) => void setHangerHardware(hangerHardware)}
        options={allHangerHardware}
        getName={getConnectingHardwareName}
        disabled={!hasEditPermission}
      />
      <NumericField
        value={price}
        label="Price per unit"
        min={0}
        disabled={!hasEditPermission}
        onSubmit={(price) => void setPrice(price)}
      />
      <PropertyTextInput
        value={notes ?? ''}
        property="Notes"
        disabled={!hasEditPermission}
        submit={(notes) => void setNotes(notes === '' ? null : notes)}
      />
      <Box sx={{ alignItems: 'center', display: 'flex' }}>
        {hasEditPermission ? (
          <Button
            variant="contained"
            size="small"
            onClick={onInsertClick}
            disabled={insertDisabled}
            sx={{ flex: 1, minHeight: 40 }}
          >
            Insert Hanger Price
          </Button>
        ) : (
          <CallToUpgrade />
        )}
      </Box>
    </PriceActionBar>
  );
}
